import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { AskAiPanel } from './AskAiPanel';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * The app shell's persistent help affordance.
 *
 * Deliberately bottom-right and fixed: it clears the sidebar's lg:ml-64 /
 * lg:ml-16 content offset without having to track it, and nothing else in the
 * app is persistently above z-50. It is also visible on mobile, where no
 * tooltip in the product is reachable at all.
 *
 * Opens on "?" as well, matching the ⌘K convention already in lib/keyboard.ts.
 */
export function AskAiLauncher() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '?') return;
      const target = e.target as HTMLElement | null;
      // Never steal the key from someone typing a literal question mark.
      if (
        target &&
        (target.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      ) {
        return;
      }
      e.preventDefault();
      setOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onOpenRequest = () => setOpen(true);
    window.addEventListener('gateco:open-ask-ai', onOpenRequest);
    return () => window.removeEventListener('gateco:open-ask-ai', onOpenRequest);
  }, []);

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => setOpen(true)}
            aria-label="Ask AI about Gateco"
            className="fixed bottom-5 right-5 z-40 flex h-11 items-center gap-2 rounded-full bg-primary pl-3.5 pr-4 text-sm font-medium text-primary-foreground shadow-lg transition-all hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <Sparkles className="h-4 w-4" />
            Ask AI
          </button>
        </TooltipTrigger>
        <TooltipContent side="left">
          Ask about setting up Gateco
          <kbd className="ml-1.5 rounded border border-border px-1 font-mono text-[10px]">
            ?
          </kbd>
        </TooltipContent>
      </Tooltip>

      <AskAiPanel open={open} onOpenChange={setOpen} />
    </>
  );
}
